import { Component, type ErrorInfo, type ReactNode } from 'react'
import { setV1Cookie } from './Interstitial'

interface Props {
  children: ReactNode
  /** 'stage' wraps the route outlet (shell survives); 'shell' wraps the app. */
  scope?: 'stage' | 'shell'
}

interface State {
  error: Error | null
}

/**
 * V2 crash surface (R20/AE3). Rendered when a surface (or the shell)
 * throws: offers reload, a one-click V1 escape, and a plain link to
 * /switch — which Caddy serves below both bundles, so it works even
 * when this boundary itself can't mount (chunk-load death).
 */
export class ErrorBoundaryV2 extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[v2] crash:', error, info.componentStack)
  }

  override render() {
    if (!this.state.error) return this.props.children
    const message = this.state.error.message || String(this.state.error)
    return (
      <section className="v2-crash" role="alert">
        <div className="v2-crash-panel fui-panel">
          <span className="fui-eyebrow">
            <span className="fui-eyebrow-dot ember" aria-hidden="true" />
            v2 · crash
          </span>
          <h1>This {this.props.scope === 'shell' ? 'shell' : 'surface'} crashed.</h1>
          <p className="v2-crash-msg">{message}</p>
          <div className="v2-interstitial-actions">
            <button type="button" className="v2-btn v2-btn-primary" onClick={() => window.location.reload()}>
              Reload V2
            </button>
            <button
              type="button"
              className="v2-btn"
              onClick={() => {
                setV1Cookie()
                window.location.reload()
              }}
            >
              Open V1
            </button>
            <a className="v2-btn" href="/switch">
              Switch page
            </a>
          </div>
          <p className="v2-interstitial-note">
            If V2 won't load at all, <code>/switch</code> works below the bundle.
          </p>
        </div>
      </section>
    )
  }
}
